/**
 * Single source of truth for the 10 supported locales.
 *
 * Every consumer (i18n bundle loader, route detector, switcher UI, drift
 * checker, hreflang generator, tests) MUST import from this file rather than
 * duplicating `["en", "ar", ...]` literals.
 *
 * Adding a locale: append a new entry to `LOCALES`, regenerate translation
 * JSON via the Phase D.2 in-conversation step, run the drift checker, and
 * update `.claude/rules/i18n-language-detection.md` if the new locale is RTL.
 */

import {
  DEFAULT_PUBLIC_LOCALE,
  PUBLIC_LOCALES,
  type PublicLocale,
} from "@hantavirus/shared/schemas/locales";

export type LocaleCode = PublicLocale;

export type LocaleDir = "ltr" | "rtl";

export interface LocaleDescriptor {
  /** Internal canonical code. Mixed case for `zh-CN` only. */
  code: LocaleCode;
  /** Lowercased URL segment (BCP-47 is case-insensitive; lowercase is the canonical wire form). */
  urlSegment: string;
  /** BCP-47 tag for `<html lang>` and `Intl.*` constructors. */
  ietf: string;
  /** Writing direction. Only `ar` is `rtl` in the current set. */
  dir: LocaleDir;
  /** Label shown in the switcher's primary line (in the locale's own script). */
  nativeLabel: string;
  /** Secondary muted label so users can find English when stuck on an unreadable script. */
  englishLabel: string;
  /** Matches a `date-fns/locale` export name for downstream consumers that import the runtime locale. */
  dateFnsLocale: string;
  /** Alias of `ietf` exposed for clarity at the `<html lang>` call site. */
  htmlLang: string;
}

export const LOCALES: readonly LocaleDescriptor[] = [
  {
    code: "en",
    urlSegment: "en",
    ietf: "en",
    dir: "ltr",
    nativeLabel: "English",
    englishLabel: "English",
    dateFnsLocale: "enUS",
    htmlLang: "en",
  },
  {
    code: "ar",
    urlSegment: "ar",
    ietf: "ar",
    dir: "rtl",
    nativeLabel: "العربية",
    englishLabel: "Arabic",
    dateFnsLocale: "ar",
    htmlLang: "ar",
  },
  {
    code: "fr",
    urlSegment: "fr",
    ietf: "fr",
    dir: "ltr",
    nativeLabel: "Français",
    englishLabel: "French",
    dateFnsLocale: "fr",
    htmlLang: "fr",
  },
  {
    code: "zh-CN",
    urlSegment: "zh-cn",
    ietf: "zh-Hans-CN",
    dir: "ltr",
    nativeLabel: "简体中文",
    englishLabel: "Chinese (Simplified)",
    dateFnsLocale: "zhCN",
    htmlLang: "zh-Hans-CN",
  },
  {
    code: "es",
    urlSegment: "es",
    ietf: "es",
    dir: "ltr",
    nativeLabel: "Español",
    englishLabel: "Spanish",
    dateFnsLocale: "es",
    htmlLang: "es",
  },
  {
    code: "de",
    urlSegment: "de",
    ietf: "de",
    dir: "ltr",
    nativeLabel: "Deutsch",
    englishLabel: "German",
    dateFnsLocale: "de",
    htmlLang: "de",
  },
  {
    code: "ru",
    urlSegment: "ru",
    ietf: "ru",
    dir: "ltr",
    nativeLabel: "Русский",
    englishLabel: "Russian",
    dateFnsLocale: "ru",
    htmlLang: "ru",
  },
  {
    code: "hi",
    urlSegment: "hi",
    ietf: "hi",
    dir: "ltr",
    nativeLabel: "हिन्दी",
    englishLabel: "Hindi",
    dateFnsLocale: "hi",
    htmlLang: "hi",
  },
  {
    code: "id",
    urlSegment: "id",
    ietf: "id",
    dir: "ltr",
    nativeLabel: "Bahasa Indonesia",
    englishLabel: "Indonesian",
    dateFnsLocale: "id",
    htmlLang: "id",
  },
  {
    code: "ms",
    urlSegment: "ms",
    ietf: "ms",
    dir: "ltr",
    nativeLabel: "Bahasa Melayu",
    englishLabel: "Malay",
    dateFnsLocale: "ms",
    htmlLang: "ms",
  },
] as const;

export const DEFAULT_LOCALE: LocaleCode = DEFAULT_PUBLIC_LOCALE;

/** Cookie name set by the switcher and the client reconciler. */
export const LOCALE_COOKIE = "hantavirus_locale";

/** localStorage key mirrored by the client reconciler. */
export const LOCALE_STORAGE_KEY = "hantavirus_locale";

const SEGMENT_TO_CODE = new Map<string, LocaleCode>(
  LOCALES.map((l) => [l.urlSegment, l.code]),
);

const CODE_TO_DESCRIPTOR = new Map<LocaleCode, LocaleDescriptor>(
  LOCALES.map((l) => [l.code, l]),
);

/** Type guard: is the value a known locale code (case-sensitive). */
export function isLocaleCode(value: unknown): value is LocaleCode {
  return typeof value === "string" && CODE_TO_DESCRIPTOR.has(value as LocaleCode);
}

/** Resolve a URL segment to a canonical code. Case-insensitive. Returns null if unrecognised. */
export function resolveSegment(segment: string | undefined | null): LocaleCode | null {
  if (!segment) return null;
  return SEGMENT_TO_CODE.get(segment.toLowerCase()) ?? null;
}

/** Get the descriptor for a code. Throws on unknown codes — callers should narrow with `isLocaleCode` first. */
export function getDescriptor(code: LocaleCode): LocaleDescriptor {
  const d = CODE_TO_DESCRIPTOR.get(code);
  if (!d) throw new Error(`Unknown locale code: ${code}`);
  return d;
}

/** Convenience: is the given code right-to-left? */
export function isRtl(code: LocaleCode): boolean {
  return getDescriptor(code).dir === "rtl";
}

/** Convenience: array of all codes in canonical order (English first). */
export const LOCALE_CODES: readonly LocaleCode[] = PUBLIC_LOCALES;

/** Convenience: set of URL segments for fast `Set.has(segment)` checks. */
export const LOCALE_URL_SEGMENTS: ReadonlySet<string> = new Set(LOCALES.map((l) => l.urlSegment));

/** Namespaces that are never machine-translated (login + admin + superadmin remain frozen). */
export const NON_TRANSLATED_NAMESPACES: readonly string[] = ["login", "admin", "superadmin"];
